/**
 * AI-generated code, moderated by lzygmanski
 */
import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {Ambulance} from 'lucide-react';
import type {ComponentRef} from 'react';
import {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {useReactToPrint} from 'react-to-print';
import {z} from 'zod';

import InfoState from '@/components/InfoState';
import {PrintLayout} from '@/components/layouts/PrintLayout';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {graphqlClient} from '@/services/graphqlClient';
import {
  type Event,
  type ResidentMedication,
  useGetResidentInfoForAmbulanceQuery,
  useListHealthLogMetricsQuery,
} from '@/services/graphqlHooks';

import {
  AmbulancePrintContactInfo,
  AmbulancePrintEvents,
  AmbulancePrintHeader,
  AmbulancePrintHealthMeasurements,
  AmbulancePrintMedicalConditions,
  AmbulancePrintMedications,
  AmbulancePrintPersonalInfo,
  AmbulancePrintSkeleton,
} from './ambulance-print';
import ResidentInput from './ResidentInput';

export type ResidentAmbulancePrintDialogProps = DialogProps;

const formSchema = z.object({
  id: z.string().trim().min(1, 'Resident is required'),
});

type FormValues = z.infer<typeof formSchema>;

const ResidentAmbulancePrintDialog = ({
  onOpenChange,
  ...props
}: ResidentAmbulancePrintDialogProps) => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const printRef = useRef<ComponentRef<'div'>>(null);
  const [selectedResidentId, setSelectedResidentId] = useState<string>('');

  const id = searchParams.get('id') || '';

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: id || '',
    },
  });

  const residentId = form.watch('id');

  useEffect(() => {
    if (!id) return;
    form.setValue('id', id, {shouldValidate: true});
    setSelectedResidentId(id);
  }, [id, form]);

  useEffect(() => {
    if (residentId && residentId !== selectedResidentId) {
      setSelectedResidentId(residentId);
      return;
    }

    if (!residentId && selectedResidentId) {
      setSelectedResidentId('');
    }
  }, [residentId, selectedResidentId]);

  const {data, isLoading} = useGetResidentInfoForAmbulanceQuery(
    graphqlClient,
    {id: selectedResidentId},
    {
      enabled: !!selectedResidentId && props.open,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  const {data: metricsData} = useListHealthLogMetricsQuery(
    graphqlClient,
    undefined,
    {
      staleTime: Infinity,
    }
  );

  const metricDefinitions = metricsData?.listHealthLogMetrics || [];

  const residentInfo = data?.getResidentInfoForAmbulance;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Ambulance_Report_${residentInfo?.name
      .firstName}_${residentInfo?.name.lastName}_${dayjs().format(
      'YYYY-MM-DD'
    )}`,
    preserveAfterPrint: true,
  });

  const handleClose = (value: boolean) => {
    form.reset();
    setSelectedResidentId('');
    onOpenChange?.(value);
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={handleClose}>
        <DialogContent className="h-[calc(100dvh-2rem)] md:h-[calc(100dvh-4rem)] md:max-w-6xl">
          <DialogHeader>
            <DialogTitle>{t('Ambulance report')}</DialogTitle>
          </DialogHeader>

          <DialogSection className="min-h-0 flex-1 gap-4 overflow-auto p-1">
            <FormField
              control={form.control}
              name="id"
              render={({field}) => (
                <FormItem>
                  <FormLabel>{t('Select Resident')}</FormLabel>
                  <FormControl>
                    <ResidentInput
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedResidentId && !residentInfo && isLoading && (
              <AmbulancePrintSkeleton />
            )}

            {!selectedResidentId && !isLoading && (
              <div className="flex h-64 items-center justify-center">
                <InfoState
                  icon={<Ambulance size={40} className="text-red-500" />}
                  title={t('Select a resident to generate report')}
                  description={t(
                    'Choose a resident from the list above to view and print their ambulance report'
                  )}
                />
              </div>
            )}

            {residentInfo && (
              <div className="w-full overflow-auto border border-border bg-muted p-5">
                <div className="mx-auto w-[210mm] bg-white p-4 shadow-default">
                  <PrintLayout ref={printRef}>
                    <div className="space-y-3">
                      <AmbulancePrintHeader
                        reportCreatedAt={residentInfo.reportCreatedAt}
                      />

                      <AmbulancePrintPersonalInfo
                        name={residentInfo.name}
                        personalNumber={residentInfo.personalNumber}
                        dateOfBirth={residentInfo.dateOfBirth}
                      />

                      <AmbulancePrintContactInfo
                        contactPersonPhoneNumber={
                          residentInfo.contactPersonPhoneNumber
                        }
                        contactPersonName={residentInfo.contactPersonName}
                        careFacilityPhoneNumber={
                          residentInfo.careFacilityPhoneNumber
                        }
                      />

                      <AmbulancePrintMedicalConditions
                        conditions={residentInfo.conditions || []}
                      />

                      <AmbulancePrintMedications
                        medications={
                          (residentInfo.residentMedications ||
                            []) as ResidentMedication[]
                        }
                      />

                      <AmbulancePrintHealthMeasurements
                        measurements={residentInfo.healthLogMeasurements || []}
                        metricDefinitions={metricDefinitions}
                      />

                      <AmbulancePrintEvents
                        events={(residentInfo.events || []) as Event[]}
                      />
                    </div>
                  </PrintLayout>
                </div>
              </div>
            )}
          </DialogSection>

          <DialogFooter>
            <Button
              variant="secondary"
              type="button"
              onClick={() => handleClose(false)}
            >
              {t('Close')}
            </Button>
            {residentInfo && (
              <Button variant="primary" type="button" onClick={handlePrint}>
                {t('Print')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default ResidentAmbulancePrintDialog;
